import type { Currency } from "../types";

const FX_TTL_MS = 1000 * 60 * 60 * 24;
const FX_FALLBACK = {
  ISK: 1,
  EUR: 0.00695,
  USD: 0.00818,
  CHF: 0.00637,
  GBP: 0.00601,
} as const satisfies Record<Currency, number>;

export interface FxRates {
  ISK: number;
  EUR: number;
  USD: number;
  CHF: number;
  GBP: number;
  fetchedAt?: number;
  date?: string;
}

export const fxFallback: FxRates = { ...FX_FALLBACK };

interface CachedFx {
  rates: { ISK?: 1; EUR: number; USD: number; CHF: number; GBP: number };
  fetchedAt: number;
  date?: string;
}

export async function loadFxRates(): Promise<FxRates> {
  try {
    const raw = localStorage.getItem("fishing-fx");
    if (raw) {
      const cached: CachedFx = JSON.parse(raw);
      if (cached?.rates && Date.now() - cached.fetchedAt < FX_TTL_MS) {
        return {
          ISK: 1,
          EUR: cached.rates.EUR,
          USD: cached.rates.USD,
          CHF: cached.rates.CHF,
          GBP: cached.rates.GBP,
          fetchedAt: cached.fetchedAt,
          date: cached.date,
        };
      }
    }
    const res = await fetch(
      "https://api.frankfurter.dev/v1/latest?base=ISK&symbols=EUR,USD,CHF,GBP",
    );
    if (!res.ok) throw new Error(`fx http ${res.status}`);
    const data = (await res.json()) as { rates: Record<string, number>; date: string };
    const rates: FxRates = {
      ISK: 1 as number,
      EUR: data.rates.EUR,
      USD: data.rates.USD,
      CHF: data.rates.CHF,
      GBP: data.rates.GBP,
      fetchedAt: Date.now(),
      date: data.date,
    };
    localStorage.setItem(
      "fishing-fx",
      JSON.stringify({ rates, fetchedAt: rates.fetchedAt, date: rates.date }),
    );
    return rates;
  } catch (err) {
    console.warn("FX fetch failed; using fallback rates", err);
    return { ...FX_FALLBACK };
  }
}

const CURRENCY_DECIMALS: Record<Currency, number> = {
  ISK: 0,
  EUR: 0,
  USD: 0,
  CHF: 0,
  GBP: 0,
};

export function formatPrice(
  isk: number,
  currency: Currency,
  rates: FxRates,
): string {
  const rate = rates[currency] ?? FX_FALLBACK[currency];
  const converted = isk * rate;
  const decimals = CURRENCY_DECIMALS[currency];
  const rounded =
    decimals === 0
      ? Math.round(converted)
      : Math.round(converted * 10 ** decimals) / 10 ** decimals;
  // Swiss locale formatting: apostrophe thousands separator + period
  // decimal separator (e.g. 32'500 / 207.03). Currency code always sits
  // AFTER the amount with a space — uniform "amount CODE" across the
  // board, no symbol prefix.
  const formatted = new Intl.NumberFormat("de-CH", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(rounded);
  // Prefix a "~" when the displayed currency is not the source (ISK) to
  // signal that the amount comes from an FX conversion and may drift.
  const prefix = currency === "ISK" ? "" : "~";
  return `${prefix}${formatted} ${currency}`;
}
